import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { FieldsetCard } from '@/components/atoms/FieldsetCard/FieldsetCard';
import { DurationInput } from '@/components/organisms/DurationInput/DurationInput';
import { Form } from '@/components/ui/form';
import { useDeskId } from '@/hooks/useDeskId';
import { useIntegrations } from '@/hooks/useIntegrations';
import { Integration } from '@/shared/types/integration';

const MIN_SECONDS = 60;
const MAX_SECONDS = 15 * 24 * 60 * 60; // 15 days
const DEFAULT_SECONDS = 60 * 60; // 1 hour

type SessionTimeoutForm = { session_timeout: number };

interface Props {
  integration: Integration;
  canWrite?: boolean;
  /** Fallback session timeout (seconds) when the integration has none set. */
  defaultSeconds?: number;
}

const FORM_ID = 'integration-session-timeout-form';

/**
 * Shared "Session timeout" card. Reads/writes `config.session_timeout` on any
 * integration; the channel decides the default via `defaultSeconds`.
 */
export const SessionTimeoutCard = ({
  integration,
  canWrite = true,
  defaultSeconds = DEFAULT_SECONDS,
}: Props) => {
  const { t } = useTranslation();
  const deskId = useDeskId();
  const { integrationId } = useParams();
  const { updateIntegration, updateStatus } = useIntegrations<Integration>(deskId, integrationId);

  const form = useForm<SessionTimeoutForm>({
    mode: 'onChange',
    defaultValues: { session_timeout: integration?.config?.session_timeout || defaultSeconds },
  });

  const { control, handleSubmit } = form;
  const sessionTimeoutValue = useWatch({ control, name: 'session_timeout' });

  const onSubmit = (values: SessionTimeoutForm) => {
    updateIntegration({
      ...integration,
      config: { ...integration.config, session_timeout: values.session_timeout },
    });
  };

  const serverSessionTimeout = integration?.config?.session_timeout || defaultSeconds;
  const isDirty = sessionTimeoutValue !== serverSessionTimeout;

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)} id={FORM_ID}>
        <div className="w-full">
          <div className="mb-2 flex items-center justify-between">
            <p className="px-1.5 text-sm leading-none font-medium text-muted-foreground">
              {t('integrations.session_timeout.title')}
            </p>
          </div>
          <FieldsetCard
            isDirty={isDirty}
            isLoading={updateStatus === 'pending'}
            disabled={!isDirty || !canWrite}
            saveButtonLabel={t('common.save')}
            formId={FORM_ID}
          >
            <div className="flex flex-col gap-4">
              <p className="text-sm text-muted-foreground">
                {t('integrations.session_timeout.subtitle_with_range')}
              </p>
              <Controller
                name="session_timeout"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <DurationInput
                    value={value || defaultSeconds}
                    onChange={onChange}
                    disabled={!canWrite}
                    minSeconds={MIN_SECONDS}
                    maxSeconds={MAX_SECONDS}
                    className="mt-0"
                  />
                )}
              />
            </div>
          </FieldsetCard>
        </div>
      </form>
    </Form>
  );
};
